(() => {
  'use strict';

  if (window.ffHeroReviews) {
    window.ffHeroReviews.init(document);
    return;
  }

  const API_ORIGIN = 'https://storefront-api.loox.io';
  const PAGE_SIZE = 24;
  const instances = new Map();
  let active = null;

  const safeMediaURL = value => {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
    } catch (_) {
      return null;
    }
  };

  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text != null) element.textContent = text;
    return element;
  };

  const reviewCard = review => {
    const card = node('article', 'ffhr-card');
    card.dataset.ffhrId = String(review.id);

    const media = Array.isArray(review.media) ? review.media : [];
    const asset = media.find(item => item && ['photo', 'video'].includes(item.type) && safeMediaURL(item.url));
    if (asset) {
      const url = safeMediaURL(asset.url);
      if (asset.type === 'video') {
        const video = node('video', 'ffhr-media');
        video.src = url;
        video.controls = true;
        video.playsInline = true;
        video.preload = 'none';
        video.setAttribute('aria-label', 'Video from a customer review');
        const poster = safeMediaURL(asset.thumbnailUrl);
        if (poster) video.poster = poster;
        card.append(video);
      } else {
        const image = node('img', 'ffhr-media');
        image.src = url;
        image.alt = 'Photo from a customer review';
        image.loading = 'lazy';
        image.decoding = 'async';
        card.append(image);
      }
    }

    const rating = Number(review.rating);
    if (Number.isFinite(rating) && rating >= 1 && rating <= 5) {
      const rounded = Math.round(rating);
      const stars = node('div', 'ffhr-stars', '★'.repeat(rounded) + '☆'.repeat(5 - rounded));
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `${rating} out of 5 stars`);
      card.append(stars);
    }
    if (typeof review.body === 'string' && review.body.trim()) {
      card.append(node('p', 'ffhr-body', review.body.trim()));
    }

    const footer = node('footer', 'ffhr-card-footer');
    const reviewer = typeof review.reviewer === 'string' ? review.reviewer : review.reviewer && review.reviewer.name;
    footer.append(node('span', 'ffhr-reviewer', typeof reviewer === 'string' && reviewer.trim() ? reviewer.trim() : 'Customer'));
    if (review.verified === true) footer.append(node('span', 'ffhr-verified', 'Verified buyer'));
    card.append(footer);
    return card;
  };

  const saveStyles = (element, properties) => properties.map(property => [property, element.style.getPropertyValue(property), element.style.getPropertyPriority(property)]);
  const restoreStyles = (element, values) => values.forEach(([property, value, priority]) => {
    if (value) element.style.setProperty(property, value, priority);
    else element.style.removeProperty(property);
  });

  const transitionTime = element => {
    const style = getComputedStyle(element);
    const milliseconds = value => value.trim().endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000;
    const durations = style.transitionDuration.split(',').map(milliseconds);
    const delays = style.transitionDelay.split(',').map(milliseconds);
    return Math.min(350, Math.max(0, ...durations.map((duration, index) => duration + delays[index % delays.length])));
  };

  class HeroReviews {
    constructor(root) {
      this.root = root;
      this.trigger = root.querySelector('[data-ffhc-open-reviews]');
      this.dialog = root.querySelector('[data-ffhc-review-dialog]');
      this.closeButton = this.dialog.querySelector('[data-ffhr-close]');
      this.scroll = this.dialog.querySelector('[data-ffhr-scroll]');
      this.list = this.dialog.querySelector('[data-ffhr-list]');
      this.status = this.dialog.querySelector('[data-ffhr-status]');
      this.more = this.dialog.querySelector('[data-ffhr-more]');
      this.sentinel = this.dialog.querySelector('[data-ffhr-sentinel]');
      this.store = this.trigger.dataset.ffhcLooxStore || '';
      this.product = this.trigger.dataset.ffhcLooxProduct || '';
      this.path = `/storefront/v1/store/${encodeURIComponent(this.store)}/product-reviews`;
      this.nextURL = new URL(this.path, API_ORIGIN);
      this.nextURL.search = new URLSearchParams({ product_id: this.product, sort: 'featured', limit: String(PAGE_SIZE), page: '1' }).toString();
      this.seen = new Set();
      this.hasMore = true;
      this.loaded = false;
      this.total = null;
      this.failed = false;
      this.destroyed = false;
      this.closing = false;
      this.request = null;
      this.generation = 0;
      this.frame = null;
      this.layoutFrame = null;
      this.closeTimer = null;
      this.lock = null;
      this.outsidePointer = false;
      this.events = new AbortController();
      const options = { signal: this.events.signal };

      this.trigger.addEventListener('click', event => {
        event.preventDefault();
        this.open();
      }, options);
      this.closeButton.addEventListener('click', () => this.close(), options);
      this.more.addEventListener('click', () => this.load(), options);
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      }, options);
      this.dialog.addEventListener('pointerdown', event => {
        this.outsidePointer = this.isOutside(event);
      }, options);
      this.dialog.addEventListener('click', event => {
        if (this.outsidePointer && this.isOutside(event)) this.close();
        this.outsidePointer = false;
      }, options);
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open && this.lock) this.finishClose(true);
      }, options);
      document.addEventListener('keydown', event => {
        if (event.key !== 'Tab' || active !== this || !this.dialog.open) return;
        const focusable = [...this.dialog.querySelectorAll('button, a[href], input, select, textarea, [tabindex], video[controls]')].filter(element => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
        const first = focusable[0] || this.closeButton;
        const last = focusable[focusable.length - 1] || first;
        const focused = document.activeElement;
        if (!this.dialog.contains(focused) || (event.shiftKey && focused === first) || (!event.shiftKey && focused === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus({ preventScroll: true });
        }
      }, options);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting) && !this.failed) this.load();
        }, { root: this.scroll, rootMargin: '240px 0px' });
        this.observer.observe(this.sentinel);
      }
      if ('ResizeObserver' in window) {
        this.listWidth = 0;
        this.cardSizes = new WeakMap();
        this.resizeObserver = new ResizeObserver(entries => {
          const changed = entries.some(entry => {
            if (entry.target === this.list) {
              const width = entry.contentRect.width;
              if (width === this.listWidth) return false;
              this.listWidth = width;
              return true;
            }
            const height = entry.target.offsetHeight;
            if (height === this.cardSizes.get(entry.target)) return false;
            this.cardSizes.set(entry.target, height);
            return true;
          });
          if (changed) this.scheduleLayout();
        });
        this.resizeObserver.observe(this.list);
      } else window.addEventListener('resize', () => this.scheduleLayout(), options);
      if (document.fonts) document.fonts.ready.then(() => this.scheduleLayout());
      this.scroll.setAttribute('aria-busy', 'false');
      this.dialog.dataset.ffhrReady = 'true';
    }

    isOutside(event) {
      if (event.target !== this.dialog) return false;
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    updateSummary(total) {
      if (Number.isFinite(total) && total >= 0) this.total = total;
      const rating = this.dialog.querySelector('[data-ffhr-rating]');
      const count = this.dialog.querySelector('[data-ffhr-count]');
      const triggerRating = this.trigger.querySelector('[data-ffhc-rating]');
      const triggerCount = this.trigger.querySelector('[data-ffhc-reviews]');
      if (rating && triggerRating) rating.textContent = triggerRating.textContent;
      if (count) {
        if (this.total !== null) count.textContent = this.total.toLocaleString();
        else if (triggerCount) count.textContent = triggerCount.textContent;
      }
    }

    lockScroll() {
      const body = document.body;
      const html = document.documentElement;
      const scrollbar = Math.max(0, window.innerWidth - html.clientWidth);
      const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
      this.lock = {
        x: window.scrollX,
        y: window.scrollY,
        body: saveStyles(body, ['position', 'top', 'left', 'right', 'width', 'overflow', 'padding-right', 'scroll-behavior']),
        html: saveStyles(html, ['scroll-behavior'])
      };
      body.style.position = 'fixed';
      body.style.top = `${-this.lock.y}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      if (scrollbar) body.style.paddingRight = `${padding + scrollbar}px`;
    }

    unlockScroll() {
      if (!this.lock) return;
      const saved = this.lock;
      this.lock = null;
      restoreStyles(document.body, saved.body);
      document.documentElement.style.setProperty('scroll-behavior', 'auto', 'important');
      document.body.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(saved.x, saved.y);
      restoreStyles(document.documentElement, saved.html);
      restoreStyles(document.body, saved.body.filter(([property]) => property === 'scroll-behavior'));
    }

    open() {
      if (this.destroyed) return;
      if (this.closing) this.finishClose(false);
      if (this.dialog.open) return;
      if (active && active !== this) active.close(false, true);
      this.updateSummary();
      this.lockScroll();
      try {
        this.dialog.showModal();
      } catch (_) {
        this.unlockScroll();
        return;
      }
      active = this;
      this.closeButton.focus({ preventScroll: true });
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.dialog.open && !this.closing && !this.destroyed) this.dialog.classList.add('ffhr--visible');
      });
      if (!this.loaded || this.failed) this.load();
      else {
        this.updateState();
        this.layoutCards();
        this.fillViewport();
      }
    }

    abortPending() {
      this.generation += 1;
      if (this.request) this.request.abort.abort();
      this.request = null;
      this.scroll.setAttribute('aria-busy', 'false');
      this.more.setAttribute('aria-disabled', 'false');
    }

    close(restoreFocus = true, immediate = false) {
      if (!this.dialog.open || this.closing) {
        if (immediate && this.lock) this.finishClose(restoreFocus);
        return;
      }
      this.closing = true;
      this.abortPending();
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.dialog.classList.remove('ffhr--visible');
      this.dialog.querySelectorAll('video').forEach(video => video.pause());
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const delay = immediate || reduced ? 0 : transitionTime(this.dialog);
      if (delay) this.closeTimer = setTimeout(() => this.finishClose(restoreFocus), delay);
      else this.finishClose(restoreFocus);
    }

    finishClose(restoreFocus) {
      if (this.closeTimer !== null) clearTimeout(this.closeTimer);
      this.closeTimer = null;
      this.closing = false;
      this.abortPending();
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.dialog.classList.remove('ffhr--visible');
      if (this.dialog.open) this.dialog.close();
      this.unlockScroll();
      if (active === this) active = null;
      if (restoreFocus && !this.destroyed && this.trigger.isConnected) this.trigger.focus({ preventScroll: true });
    }

    validateNext(value, current) {
      if (typeof value !== 'string' || !value) throw new Error('Missing review page');
      const next = new URL(value, API_ORIGIN);
      if (next.origin !== API_ORIGIN || next.username || next.password || next.pathname !== this.path) throw new Error('Invalid review page');
      const page = Number(next.searchParams.get('page'));
      const currentPage = Number(current.searchParams.get('page'));
      if (!Number.isInteger(page) || page !== currentPage + 1) throw new Error('Invalid review page number');
      if (next.searchParams.get('product_id') !== this.product || next.searchParams.get('sort') !== 'featured' || next.searchParams.get('limit') !== String(PAGE_SIZE)) throw new Error('Invalid review filters');
      next.hash = '';
      return next;
    }

    updateState() {
      this.more.setAttribute('aria-disabled', this.request ? 'true' : 'false');
      if (this.request) {
        this.status.textContent = this.seen.size ? 'Loading more reviews…' : 'Loading reviews…';
        this.more.hidden = !this.seen.size && document.activeElement !== this.more;
        this.more.textContent = 'Loading…';
      } else if (this.failed) {
        this.status.textContent = 'Reviews could not load. Please try again.';
        this.more.hidden = false;
        this.more.textContent = 'Try again';
      } else if (!this.hasMore) {
        this.status.textContent = this.seen.size ? 'You’ve reached the end of the reviews.' : 'No reviews to show yet.';
        if (document.activeElement === this.more) this.scroll.focus({ preventScroll: true });
        this.more.hidden = true;
        this.more.textContent = 'Load more reviews';
      } else {
        this.status.textContent = `${this.seen.size.toLocaleString()} reviews loaded.`;
        this.more.hidden = false;
        this.more.textContent = 'Load more reviews';
      }
    }

    fillViewport() {
      if (!this.dialog.open || this.closing || this.failed || this.request || !this.hasMore) return;
      const remaining = this.scroll.scrollHeight - this.scroll.scrollTop - this.scroll.clientHeight;
      if (remaining < 240) this.load();
    }

    layoutCards() {
      if (this.destroyed || !this.dialog.open) return;
      const masonry = window.matchMedia('(min-width: 600px)').matches;
      [...this.list.children].forEach(card => {
        if (masonry) card.style.gridRowEnd = `span ${Math.ceil(card.offsetHeight + 18)}`;
        else card.style.removeProperty('grid-row-end');
      });
    }

    scheduleLayout() {
      if (this.destroyed || !this.dialog.open || this.layoutFrame !== null) return;
      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = null;
        this.layoutCards();
        this.fillViewport();
      });
    }

    async load() {
      if (this.destroyed || !this.dialog.open || this.closing || this.request || !this.hasMore) return;
      if (!this.store || !this.product) {
        this.status.textContent = 'Reviews are temporarily unavailable.';
        this.more.hidden = true;
        return;
      }
      const request = { abort: new AbortController(), generation: this.generation, url: this.nextURL };
      this.request = request;
      this.failed = false;
      this.scroll.setAttribute('aria-busy', 'true');
      this.updateState();
      try {
        const response = await fetch(request.url.href, { credentials: 'omit', signal: request.abort.signal });
        if (!response.ok) throw new Error('Review request failed');
        const data = await response.json();
        if (request.generation !== this.generation || this.destroyed || this.request !== request) return;
        if (!data || !Array.isArray(data.reviews) || !data.pagination || typeof data.pagination.hasMore !== 'boolean') throw new Error('Invalid reviews');
        const added = [];
        const pageIDs = new Set();
        data.reviews.forEach(review => {
          if (!review || (typeof review.id !== 'string' && typeof review.id !== 'number')) return;
          const id = String(review.id);
          if (!id || this.seen.has(id) || pageIDs.has(id)) return;
          pageIDs.add(id);
          added.push(review);
        });
        const hasMore = data.pagination.hasMore && added.length > 0;
        const next = hasMore ? this.validateNext(data.pagination.nextPageUrl, request.url) : null;
        const fragment = document.createDocumentFragment();
        added.forEach(review => fragment.append(reviewCard(review)));
        this.list.append(fragment);
        if (this.resizeObserver) [...this.list.children].forEach(card => this.resizeObserver.observe(card));
        this.layoutCards();
        added.forEach(review => this.seen.add(String(review.id)));
        this.hasMore = hasMore;
        if (next) this.nextURL = next;
        this.loaded = true;
        this.updateSummary(Number(data.pagination.total));
      } catch (error) {
        if (request.generation !== this.generation || this.destroyed || this.request !== request || error.name === 'AbortError') return;
        this.failed = true;
      } finally {
        if (this.request === request) {
          this.request = null;
          this.scroll.setAttribute('aria-busy', 'false');
          this.updateState();
          if (!this.failed) this.fillViewport();
        }
      }
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.close(false, true);
      this.abortPending();
      this.events.abort();
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.layoutFrame !== null) cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = null;
      this.dialog.removeAttribute('data-ffhr-ready');
      instances.delete(this.root);
    }
  }

  const init = scope => {
    const roots = [];
    if (scope.matches && scope.matches('.ffhc')) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll('.ffhc'));
    roots.forEach(root => {
      if (instances.has(root)) return;
      const trigger = root.querySelector('[data-ffhc-open-reviews]');
      const dialog = root.querySelector('[data-ffhc-review-dialog]');
      if (!trigger || !dialog || !['data-ffhr-close', 'data-ffhr-scroll', 'data-ffhr-list', 'data-ffhr-status', 'data-ffhr-more', 'data-ffhr-sentinel'].every(attribute => dialog.querySelector(`[${attribute}]`))) return;
      instances.set(root, new HeroReviews(root));
    });
  };

  const destroy = event => {
    const sectionID = event.detail && event.detail.sectionId;
    instances.forEach(instance => {
      const targetContainsRoot = event.target && event.target.contains && event.target.contains(instance.root);
      const wrapper = instance.root.closest('[id^="shopify-section-"]');
      const matchesID = sectionID && ((wrapper && wrapper.id === `shopify-section-${sectionID}`) || instance.dialog.id === `ffhc-reviews-${sectionID}`);
      if (sectionID ? matchesID : targetContainsRoot) instance.destroy();
    });
  };

  window.ffHeroReviews = { init, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', destroy);
})();
